<template>
	<article>
		<span :class="[
			'command-method',
			`method-${command.method.toLowerCase()}`,
		]">
			{{ command.method }}
		</span>
		<span class="command-resource">
			{{ command.resource }}
		</span>
		<time>
			{{ createdLocalTime }} #{{ command.id }}
		</time>

		<span v-if="command.filter" class="command-filters">
			<span v-for="(filter, index) in command.filter" :key="index">
				{{ filter.key }} = {{ filter.value }}
			</span>
		</span>

		<details v-if="body">
			<summary>
				Body
			</summary>

			<BaseJsonAceEditor
				v-model:value="body"
				:id="`commands_list_item_body_${command.id}`"
				readonly
			/>
		</details>

		<div v-if="isPartiallyAnswered">
			Uploading answer, page {{ command.count_answer_pages }}...
		</div>

		<details v-if="answer" :title="answeredLocalTime">
			<summary :class="[`command-answer-status-${answerStatusType}`]">
				Answer ({{command.answer_status }})
			</summary>

			<template v-if="isPagedAnswer">
				<p>
					The answer is too large for immediate loading.
					It consists of {{ command.count_answer_pages }} pages.
				</p>
				<p>
					<button @click.prevent="loadAnswerPages" :disabled="isLoadPagedAnswerDisabled">
						Load anyway
					</button>
				</p>
			</template>
			<BaseJsonAceEditor
				v-else
				v-model:value="answer"
				:id="`commands_list_item_answer_${command.id}`"
				readonly
			/>
		</details>

		<div class="actions">
			<button @click.prevent="requestReplay">
				Replay
			</button>

			<button @click.prevent="requestDelete" title="Delete" class="command-delete">
				{{ deleteButtonLabel }}
			</button>
		</div>
	</article>
</template>

<script>
import BaseJsonAceEditor from '@/core/components/BaseJsonAceEditor'

export default {
	name: 'CommandsListItem',
	components: {
		BaseJsonAceEditor,
	},
	props: {
		command: {
			type: Object,
			required: true,
		},
	},
	emits: [
		'requestDelete',
		'requestReplay',
	],
	data() {
		return {
			deleteNeedsConfirmation: true,
			isLoadPagedAnswerDisabled: false,
		};
	},
	computed: {
		createdLocalTime() {
			const date = new Date(`${this.command.created_time}Z`);

			return date.toLocaleString();
		},
		canHaveBody() {
			return this.command.method in {
				'POST': true,
				'PUT': true,
			};
		},
		body() {
			if (!this.command.body || !this.canHaveBody) {
				return null;
			}

			return JSON.stringify(this.command.body, null, '\t');
		},
		answer() {
			if (!this.command.answer) {
				return null;
			}

			return JSON.stringify(this.command.answer, null, '\t');
		},
		isPartiallyAnswered() {
			return (!this.command.answer && this.command.count_answer_pages);
		},
		isPagedAnswer() {
			if (this.command.answer && this.command.answer.length) {
				return false;
			}

			return !!this.command.count_answer_pages > 0;
		},
		answerStatusType() {
			if (!this.command.answer_status) {
				return '';
			}

			return (this.command.answer_status + '').substring(0, 1);
		},
		answeredLocalTime() {
			const date = new Date(`${this.command.answer_time}Z`);

			return date.toLocaleString();
		},
		deleteButtonLabel() {
			if (this.deleteNeedsConfirmation) {
				return 'x';
			}

			return 'Confirm?';
		},
	},
	methods: {
		requestDelete() {
			if (this.deleteNeedsConfirmation) {
				this.deleteNeedsConfirmation = false;

				setTimeout(() => {
					this.deleteNeedsConfirmation = true;
				}, 5000);

			} else {
				this.$emit('requestDelete');
			}
		},
		requestReplay() {
			this.$emit('requestReplay');
		},
		loadAnswerPages() {
			this.isLoadPagedAnswerDisabled = true;
			this.$emit('requestAnswerPages');
		},
	},
};
</script>

<style scoped>
article {
	position: relative;
	margin: 0.5em 0;
	padding: 0.3em;
	background: #fff;
	border-radius: 4px;
}

.command-method {
	display: inline-block;
	vertical-align: middle;
	margin: 0.2em 0.5em 0.2em 0;
	padding: 0.2em 0.3em;
	border-radius: 4px;
	font-size: 0.7em;
	text-shadow: 1px 1px 1px #fff;
}

.method-get {
	background: #abe8aa;
}

.method-post {
	background: #c9aae8;
}

.method-put {
	background: #e8caaa;
}

.method-delete {
	background: #e8aaaa;
}

.command-resource {
	display: inline-block;
	vertical-align: middle;
}

article > time {
	display: block;
	color: #aaa;
	font-style: italic;
	font-size: 0.9em;
}

.command-filters > span {
	display: inline-block;
	margin: 0.2em 0.5em 0.2em 0;
	padding: 0.2em 0.3em;
	background: #eee;
	border-radius: 4px;
	font-size: 0.8em;
}

.command-filters > span:not(:last-of-type)::after {
	content: ', ';
}

article > details > summary {
	cursor: pointer;
}

.command-answer-status-2 {
	color: #0da20a;
}

.command-answer-status-4, .command-answer-status-5 {
	color: #a20a0a;
}

.actions {
	position: absolute;
	top: 5px;
	right: 5px;
	padding-bottom: 6px;
	line-height: 0.7;
}

.actions button {
	margin-left: 5px;
}
</style>
